/**
 * Mapa de influência (platform design §17.10): the campaign at a glance, and
 * the screen a caller lands on. One request fills every card, so the loading
 * state is a skeleton of the whole layout and a failure replaces all of it.
 *
 * The window, dimension and metric live in the URL and are remembered per
 * screen (`useViewSearch`): changing them re-reads the same screen. Each figure's delta is measured against the window of the same
 * length just before it.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { Page, PageHeader } from '../../../chrome/page';
import { useViewSearch } from '../../../chrome/viewSearch';
import { QueryStates } from '../../../chrome/state';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import { rangeFor, SPANS, type Span } from './figures';
import { useBreakdown, useOverview } from '../api/queries';
import { breakdownMetricSchema, type BreakdownMetric } from '../api/schemas';
import { ov } from './messages';
import { OverviewSkeleton } from './OverviewSkeleton';
import { OverviewView } from './OverviewView';

const DEFAULT_SPAN: Span = '1d';
const DEFAULT_METRIC: BreakdownMetric = 'views';

export function Overview(props: { campaignId: string; tenantId: string }): ReactNode {
  const [values, set] = useViewSearch(`overview.${props.campaignId}`, {
    span: { default: DEFAULT_SPAN, values: SPANS },
    dimension: { default: '' },
    metric: { default: DEFAULT_METRIC, values: breakdownMetricSchema.options },
  });
  const span = values.span as Span;
  // The range is fixed per span: a range recomputed on every render would be
  // a new query key each time.
  const [now] = useState(() => new Date());
  const range = useMemo(() => rangeFor(span, now), [span, now]);
  const selection = { span, range };
  // The influence map's own dimension and metric: a change refetches only
  // that card (`InfluenceMap`'s own query), never the headline figures.
  const dimensionId = values.dimension === '' ? null : values.dimension;
  const setDimensionId = (next: string | null): void => set({ dimension: next ?? '' });
  const metric = values.metric as BreakdownMetric;
  const setMetric = (next: BreakdownMetric): void => set({ metric: next });
  const reading = useOverview(props.campaignId, props.tenantId, selection.range);
  const breakdown = useBreakdown(props.campaignId, props.tenantId, {
    metric,
    dimensionId,
    window: selection.range.window,
  });

  return (
    <Page>
      <PageHeader
        title={ov('heading')}
        actions={
          <ToggleGroup
            type="single"
            spacing={1}
            aria-label={ov('window.label')}
            value={selection.span}
            // Radix reports an empty string when the selected item is pressed again;
            // a segmented selector always has one option chosen.
            onValueChange={(next) => {
              if (next !== '') set({ span: next });
            }}
            className="bg-secondary rounded-pill p-1"
          >
            {SPANS.map((span) => (
              <ToggleGroupItem
                key={span}
                value={span}
                aria-label={ov(`window.${span}`)}
                className={SEGMENTED_ITEM_CLASSNAME}
              >
                {span}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        }
      />
      <QueryStates
        query={reading}
        empty={null}
        isEmpty={() => false}
        loading={<OverviewSkeleton />}
      >
        {(data) => (
          <OverviewView
            reading={data}
            span={selection.span}
            campaignId={props.campaignId}
            breakdown={breakdown}
            dimensionId={dimensionId}
            onDimensionChange={setDimensionId}
            metric={metric}
            onMetricChange={setMetric}
          />
        )}
      </QueryStates>
    </Page>
  );
}
